import { useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { useProviderServices } from '../../../app/providers/AppServicesContext';
import { acquireApiAccessTokenAsync } from '../../../services/auth/AuthService';
import { alerts } from '../../../shared/ui/Alerts';
import { User } from '../../../domain/users/models/User';
import {
    setUsers,
    setSelectedUser,
    setTotalItems,
    setPageNumber,
    setPageSize,
    setSearchTerm,
    setIsLoading,
    setError,
} from '../../../redux/features/users/usersSlice';
import { CreateUserCommand, EditUserCommand } from '../../../services/providers/progpt/dtos/requests/ProGPTUserRequest';

export interface UseUserManagementVm {
    users: User[];
    selectedUser: User | undefined;
    totalItems: number;
    pageNumber: number;
    pageSize: number;
    searchTerm: string;
    isLoading: boolean;
    error: string | undefined;
    onFetchUsers: () => Promise<void>;
    onCreateUser: (command: CreateUserCommand) => Promise<void>;
    onUpdateUser: (userId: string, command: EditUserCommand) => Promise<void>;
    onSelectUser: (user: User | undefined) => void;
    onPageNumberChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    onSearchTermChange: (term: string) => void;
}

export const useUserManagement = (): UseUserManagementVm => {
    const dispatch = useAppDispatch();
    const providerServices = useProviderServices();
    const { instance, inProgress } = useMsal();

    const users = useAppSelector((state) => state.users.users);
    const selectedUser = useAppSelector((state) => state.users.selectedUser);
    const totalItems = useAppSelector((state) => state.users.totalItems);
    const pageNumber = useAppSelector((state) => state.users.pageNumber);
    const pageSize = useAppSelector((state) => state.users.pageSize);
    const searchTerm = useAppSelector((state) => state.users.searchTerm);
    const isLoading = useAppSelector((state) => state.users.isLoading);
    const error = useAppSelector((state) => state.users.error);

    const onFetchUsers = useCallback(async (): Promise<void> => {
        dispatch(setIsLoading(true));
        dispatch(setError(undefined));

        try {
            const accessToken = await acquireApiAccessTokenAsync({
                msalInstance: instance,
            });

            const response = await providerServices.proGPT.users.getAllUsersAsync(
                accessToken,
                pageNumber,
                pageSize,
                searchTerm,
            );
            dispatch(setUsers(response.value.items));
            dispatch(setTotalItems(response.value.totalRecords));
        } catch {
            dispatch(setError('Failed to fetch users.'));
            alerts.error('Failed to fetch users. Please try again.');
        } finally {
            dispatch(setIsLoading(false));
        }
    }, [dispatch, instance, inProgress, pageNumber, pageSize, providerServices.proGPT.users, searchTerm]);

    const onCreateUser = useCallback(
        async (command: CreateUserCommand): Promise<void> => {
            dispatch(setIsLoading(true));
            dispatch(setError(undefined));

            try {
                const accessToken = await acquireApiAccessTokenAsync({
                    msalInstance: instance,
                });

                await providerServices.proGPT.users.createUserAsync(command, accessToken);
                alerts.success('User created successfully.');
                await onFetchUsers();
            } catch {
                dispatch(setError('Failed to create user.'));
                alerts.error('Failed to create user. Please try again.');
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [dispatch, instance, inProgress, onFetchUsers, providerServices.proGPT.users],
    );

    const onUpdateUser = useCallback(
        async (userId: string, command: EditUserCommand): Promise<void> => {
            dispatch(setIsLoading(true));
            dispatch(setError(undefined));
            try {
                const accessToken = await acquireApiAccessTokenAsync({
                    msalInstance: instance,
                });

                const updatedUser = await providerServices.proGPT.users.updateUserAsync(userId, command, accessToken);
                dispatch(setSelectedUser(updatedUser.value));
                alerts.success('User updated successfully.');
                await onFetchUsers();
            } catch {
                dispatch(setError('Failed to update user.'));
                alerts.error('Failed to update user. Please try again.');
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [dispatch, instance, inProgress, onFetchUsers, providerServices.proGPT.users],
    );

    const onSelectUser = useCallback(
        (user: User | undefined): void => {
            dispatch(setSelectedUser(user));
        },
        [dispatch],
    );

    const onPageNumberChange = useCallback(
        (page: number): void => {
            dispatch(setPageNumber(page));
        },
        [dispatch],
    );

    const onPageSizeChange = useCallback(
        (size: number): void => {
            dispatch(setPageSize(size));
        },
        [dispatch],
    );

    const onSearchTermChange = useCallback(
        (term: string): void => {
            dispatch(setSearchTerm(term));
            dispatch(setPageNumber(1));
        },
        [dispatch],
    );

    return useMemo(
        () => ({
            users,
            selectedUser,
            totalItems,
            pageNumber,
            pageSize,
            searchTerm,
            isLoading,
            error,
            onFetchUsers,
            onCreateUser,
            onUpdateUser,
            onSelectUser,
            onPageNumberChange,
            onPageSizeChange,
            onSearchTermChange,
        }),
        [
            users,
            selectedUser,
            totalItems,
            pageNumber,
            pageSize,
            searchTerm,
            isLoading,
            error,
            onFetchUsers,
            onCreateUser,
            onUpdateUser,
            onSelectUser,
            onPageNumberChange,
            onPageSizeChange,
            onSearchTermChange,
        ],
    );
};

export default useUserManagement;
